// /src/pages/UserSpecialPermissions.jsx
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import toast from "react-hot-toast";

import {
  useGetEmployeeEffectivePermissionsQuery,
  useSetUserPermissionsMutation,
} from "../toolkitServices/userPermissionApi";
import { MODULES } from "../utils/helpers";



const EMPTY_PERMS = {
  view: false,
  create: false,
  edit: false,
  delete: false,
  approve: false,
};

const blankPermissions = () =>
  MODULES.map((m) => ({
    moduleKey: m.key,
    permissions: { ...EMPTY_PERMS },
  }));

const UserSpecialPermissions = () => {
  // URL se employeeId ayega
  const { employeeId } = useParams();

  // 🔹 Combined API: employee + rolePerms + userOverrides + effectivePerms
  const {
    data: permResp,
    isLoading: isLoadingPerms,
    isFetching,
    error: permError,
  } = useGetEmployeeEffectivePermissionsQuery(employeeId, {
    skip: !employeeId,
  });

  const [saveUserPerms, { isLoading: isSaving, error: saveError }] =
    useSetUserPermissionsMutation();

  const [rows, setRows] = useState(blankPermissions());

  // ---- Derive objects from response ----
  const details = permResp?.data || {};
  const employee = details.employee || null;
  const effectivePermissions = details.effectivePermissions || []; // ⭐ UI prefill
  const rolePerms = details.rolePermissions || [];

  const loginUserId =
    employee?.userId?._id || employee?.userId || null; // depending on shape

  // rolePerms ko map me convert (base rights)
  const rolePermMap = useMemo(() => {
    const map = {};
    rolePerms.forEach((rp) => {
      map[rp.moduleKey] = {
        view: !!rp.permissions?.view,
        create: !!rp.permissions?.create,
        edit: !!rp.permissions?.edit,
        delete: !!rp.permissions?.delete,
        approve: !!rp.permissions?.approve,
      };
    });
    return map;
  }, [rolePerms]);

  // ---- EFFECTS ----

  useEffect(() => {
    if (permError) {
      toast.error(
        permError?.data?.message ||
          permError?.error ||
          "Failed to load employee permissions"
      );
    }
  }, [permError]);

  useEffect(() => {
    if (employee && !loginUserId) {
      toast(
        "Is employee ke liye login create nahi hai. Special permissions tabhi kaam karegi jab login ho.",
        { icon: "ℹ️" }
      );
    }
  }, [employee, loginUserId]);

  // ⭐ Effective perms ko UI me prefill karna
  useEffect(() => {
    // agar kuch nahi mila, sab false rakho
    if (!effectivePermissions || effectivePermissions.length === 0) {
      setRows(blankPermissions());
      return;
    }

    const map = {};
    effectivePermissions.forEach((p) => {
      map[p.moduleKey] = {
        moduleKey: p.moduleKey,
        permissions: {
          view: !!p.permissions?.view,
          create: !!p.permissions?.create,
          edit: !!p.permissions?.edit,
          delete: !!p.permissions?.delete,
          approve: !!p.permissions?.approve,
        },
      };
    });

    setRows(
      MODULES.map(
        (m) =>
          map[m.key] || {
            moduleKey: m.key,
            permissions: { ...EMPTY_PERMS },
          }
      )
    );
  }, [effectivePermissions]);

  useEffect(() => {
    if (saveError) {
      toast.error(saveError?.data?.message || "Failed to save permissions");
    }
  }, [saveError]);

  const loading = isLoadingPerms || isFetching || isSaving;

  // ---- toggle helpers ----
  const togglePermission = (moduleKey, field) => {
    setRows((prev) =>
      prev.map((mod) =>
        mod.moduleKey === moduleKey
          ? {
              ...mod,
              permissions: {
                ...mod.permissions,
                [field]: !mod.permissions[field],
              },
            }
          : mod
      )
    );
  };

  const toggleAll = (moduleKey) => {
    setRows((prev) =>
      prev.map((mod) => {
        if (mod.moduleKey !== moduleKey) return mod;
        const cur = mod.permissions;
        const allChecked =
          cur.view && cur.create && cur.edit && cur.delete && cur.approve;

        return {
          ...mod,
          permissions: {
            view: !allChecked,
            create: !allChecked,
            edit: !allChecked,
            delete: !allChecked,
            approve: !allChecked,
          },
        };
      })
    );
  };

  // ---- SAVE ----
  const handleSave = async () => {
    if (!loginUserId) {
      toast.error("Is employee ke liye koi login user nahi mila");
      return;
    }

    // ✅ Yahan se hum ROLE se compare karenge:
    //  - agar row == rolePerm -> override ki zarurat nahi
    //  - agar row != rolePerm -> override create kare (chahe sab false ho)
    const permissionsPayload = rows
      .map((r) => {
        const base = rolePermMap[r.moduleKey] || EMPTY_PERMS;
        const cur = r.permissions;

        const isSameAsRole =
          !!base.view === !!cur.view &&
          !!base.create === !!cur.create &&
          !!base.edit === !!cur.edit &&
          !!base.delete === !!cur.delete &&
          !!base.approve === !!cur.approve;

        if (isSameAsRole) {
          // role jaisa hi hai, override ki zaroorat nahi
          return null;
        }

        // different hai -> override record banao
        return {
          moduleKey: r.moduleKey,
          permissions: cur,
        };
      })
      .filter(Boolean); // null hatao

    try {
      await saveUserPerms({
        userId: loginUserId,
        permissions: permissionsPayload,
      }).unwrap();
      toast.success("Special permissions updated");
    } catch {
      // error effect me handle ho jayega
    }
  };

  const employeeName = useMemo(() => {
    if (!employee) return "";
    return (
      employee.fullName ||
      `${employee.firstName || ""} ${employee.lastName || ""}`.trim()
    );
  }, [employee]);

  const roleName = employee?.role?.roleName || "—";

  return (
    <div className="px-2 py-1">
      {/* HEADER */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-5xl mx-auto bg-gradient-to-r from-[#7d6bd9] to-[#5c4ab5] text-white px-3 py-3 rounded-xl shadow flex items-center gap-4"
      >
        <Link
          to="/organisation/employee-list"
          className="h-9 w-9 flex items-center justify-center bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg transition"
        >
          <FaArrowLeft className="text-white text-sm" />
        </Link>

        <div className="flex flex-col">
          <h1 className="text-sm md:text-lg font-semibold tracking-wide">
            Special Permissions
          </h1>
          <p className="text-[10px] md:text-xs text-violet-100">
            Role ke base rights par, is user ke liye customize karo
          </p>

          {employeeName && (
            <p className="text-[10px] md:text-xs text-violet-50 mt-0.5">
              User: <span className="font-semibold">{employeeName}</span>{" "}
              {roleName && (
                <span className="opacity-80">
                  {" "}
                  • Role: <b>{roleName}</b>
                </span>
              )}
            </p>
          )}
        </div>
      </motion.div>

      {/* MAIN CARD */}
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.25 }}
        className="max-w-5xl mx-auto mt-2 bg-white rounded-xl shadow-xl border px-3 py-4"
      >
        {/* Small note */}
        <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-800">
          <b>Logic:</b> Role se jo rights milte hain woh{" "}
          <b>default</b> hote hain. Yahan jo matrix dikh rahi hai, woh abhi
          effective rights hai. Agar aap koi module change karke Save karte
          ho:
          <ul className="list-disc list-inside mt-1">
            <li>Same as role hai → override record nahi banega.</li>
            <li>
              Role se different hai (chahe sab false hi kyu na ho) →{" "}
              <b>override</b> banega aur role ignore ho jayega us module ke liye.
            </li>
          </ul>
        </div>

        {/* TABLE */}
        <section>
          <h2 className="text-sm font-semibold text-[#4b3d91] border-l-4 border-[#6b59c6] pl-2 mb-2">
            Special Permissions for this User
          </h2>

          <div className="border rounded-xl shadow bg-gray-50">
            <div className="overflow-y-auto" style={{ maxHeight: "260px" }}>
              <table className="w-full text-[12px]">
                <thead className="sticky top-0 bg-white border-b shadow-sm">
                  <tr className="text-gray-700">
                    <th className="py-2 px-4 text-left">Module</th>
                    <th className="py-2 text-center">All</th>
                    <th className="py-2">View</th>
                    <th className="py-2">Create</th>
                    <th className="py-2">Edit</th>
                    <th className="py-2">Delete</th>
                    <th className="py-2">Approve</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const allSelected =
                      row.permissions.view &&
                      row.permissions.create &&
                      row.permissions.edit &&
                      row.permissions.delete &&
                      row.permissions.approve;

                    const moduleMeta = MODULES.find(
                      (m) => m.key === row.moduleKey
                    );

                    return (
                      <tr
                        key={row.moduleKey}
                        className="border-b hover:bg-violet-50/40 transition"
                      >
                        <td className="py-2.5 px-4 font-medium text-gray-800 whitespace-nowrap">
                          {moduleMeta?.label || row.moduleKey}
                        </td>

                        {/* ALL toggle */}
                        <td className="text-center">
                          <span
                            onClick={() => toggleAll(row.moduleKey)}
                            className={`inline-flex items-center justify-center h-6 w-6 border rounded-full cursor-pointer shadow-sm hover:shadow-md transition ${
                              allSelected
                                ? "border-[#6b59c6] text-[#6b59c6]"
                                : "border-slate-300 text-slate-400"
                            }`}
                          >
                            {allSelected ? "✔" : ""}
                          </span>
                        </td>

                        {/* Individual flags */}
                        {["view", "create", "edit", "delete", "approve"].map(
                          (field) => (
                            <td key={field} className="text-center">
                              <span
                                onClick={() =>
                                  togglePermission(row.moduleKey, field)
                                }
                                className={`inline-flex items-center justify-center h-6 w-6 text-[11px] bg-white border rounded-full cursor-pointer shadow-sm hover:shadow-md transition ${
                                  row.permissions[field]
                                    ? "border-[#6b59c6] text-[#6b59c6]"
                                    : "border-slate-300 text-slate-400"
                                }`}
                              >
                                {row.permissions[field] ? "✔" : ""}
                              </span>
                            </td>
                          )
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* BUTTONS */}
        <div className="flex gap-3 pt-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={loading}
            className="px-4 py-2 bg-[#6b59c6] text-white rounded-lg text-sm font-medium hover:bg-[#5943b0] shadow-sm hover:shadow-md transition disabled:opacity-60"
          >
            {loading ? "Saving..." : "Save Special Permissions"}
          </button>

          <Link
            to="/organisation/employee-list"
            className="px-4 py-2 border rounded-lg bg-white hover:bg-gray-100 text-sm shadow-sm transition"
          >
            Cancel
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default UserSpecialPermissions;
